import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../../common/utils';
import { sendTrackEvent } from '../../../../common/utils/analytics-client';
import { captureCallerSignals } from '../../../../common/utils/caller-attribution';
import {
	isProcessingAllowedByPreferences,
	isProcessingAllowedByPreferencesMaybeSync,
	isPromise,
} from '../../../../common/utils/item-processing';
import { Logger } from '../../../../common/utils/logger';
import { ItemKeyCache } from '../../../../services/item-key-cache-service';
import {
	shouldReportUncategorizedItemSet,
	uncategorizedItemSetOnSent,
} from '../../../../services/uncategorized-item-report-cache';
import { type AtlBrowserStorage, WebStorageType } from '../../../../services/web-storage-service';
import { reportSetItemError } from '../../../../services/web-storage-service/utils/report-set-item-error';
import { CategoryStorageType } from '../../../../types';

// GET SET COOKIE ANALOGUE
export const setItemOverride =
	(
		bscStorage: AtlBrowserStorage,
		originalSetItem: (key: string, value: string) => void,
		storageType: WebStorageType,
	) =>
	(originalItemKey: string, originalItemValue: string): void => {
		const categoryStorageType =
			storageType === WebStorageType.LocalStorage
				? CategoryStorageType.LocalStorage
				: CategoryStorageType.SessionStorage;
		const storageContext = bscStorage.storage;
		let itemSetByPackage = false;
		itemSetByPackage = ItemKeyCache.wasSetByPackage(categoryStorageType, originalItemKey);

		// `disableWebStorageConsentChecks` is read from the package global state so the
		// behaviour stays consistent with other consent-check flags (rather than being
		// threaded through props/options on every call site). When set, the override
		// always passes through to the underlying storage and only emits analytics
		// events — `AtlBrowserStorage` behaviour is preserved.
		let disableWebStorageConsentChecks =
			getBscGlobalState().disableWebStorageConsentChecks ?? false;

		if (fg('jfp-magma-selective-storage-enforcement')) {
			// `disableWebStorageConsentChecks` skips the consent pipeline (analytics-only),
			// but `__UNSAFE_shouldEnforceKeyForRollout(key, type) === true` re-enables enforcement for this key.
			const categoryStorageType =
				storageType === WebStorageType.LocalStorage
					? CategoryStorageType.LocalStorage
					: CategoryStorageType.SessionStorage;
			const __UNSAFE_shouldEnforceKeyForRollout =
				getBscGlobalState().__UNSAFE_shouldEnforceKeyForRollout;
			const enforceThisKey =
				__UNSAFE_shouldEnforceKeyForRollout?.(originalItemKey, categoryStorageType) === true;
			disableWebStorageConsentChecks =
				(getBscGlobalState().disableWebStorageConsentChecks ?? false) && !enforceThisKey;
		}
		if (disableWebStorageConsentChecks) {
			try {
				ItemKeyCache.markSetByPackage(categoryStorageType, originalItemKey);
				originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
			} catch (e: any) {
				reportSetItemError({
					message: `${storageType} failed to setItem with consent checks disabled. ${
						e.message || ''
					}`,
					attributes: {
						outsidePackage: !itemSetByPackage,
						storageType,
						consentChecksDisabled: true,
					},
					error: e,
				});
			}
			let wasCheckAsync: boolean = true;
			try {
				const productName = getBscGlobalState().product;
				const {
					allowUncategorizedCookiesInOverrides,
					allowUncategorizedItemsInWebStorageOverrides,
				} = getBscGlobalState();
				// trigger the callbacks to emit analytics events but do nothing with the result.
				isProcessingAllowedByPreferences({
					itemKey: originalItemKey,
					allowedCallback: ({ wasAsync }) => {
						wasCheckAsync = wasAsync;
						sendPackageOperationalEvent({
							action: 'usedAtlBrowserStorageSetItem',
							attributes: {
								itemKey: originalItemKey,
								outsidePackage: !itemSetByPackage,
								storageType,
								wasAsync,
								consentChecksDisabled: true,
							},
						});
					},
					blockedCallback: ({ itemHasCategory, itemCategory, wasAsync }) => {
						wasCheckAsync = wasAsync;
						sendPackageOperationalEvent({
							action: 'usedAtlBrowserStorageSetItem',
							attributes: {
								itemKey: originalItemKey,
								outsidePackage: !itemSetByPackage,
								storageType,
								wasAsync,
								consentChecksDisabled: true,
							},
						});

						if (
							!itemHasCategory &&
							shouldReportUncategorizedItemSet(categoryStorageType, originalItemKey)
						) {
							sendTrackEvent({
								source: 'package',
								action: 'package',
								actionSubject: 'uncategorizedWebStorageItemSet',
								attributes: {
									storageType,
									productName,
									originalItemKey,
									itemCategory: itemCategory ?? null,
									allowUncategorizedCookiesInOverrides,
									allowUncategorizedItemsInWebStorageOverrides,
									consentChecksDisabled: true,
									wasAsync,
									cacheLoadedDate: getBscGlobalState().cacheLoadedDate ?? null,
								},
								onSent: uncategorizedItemSetOnSent(categoryStorageType, originalItemKey),
							});
						}
					},
					storageType: categoryStorageType,
					productName,
				});
			} catch (e: any) {
				reportSetItemError({
					message: `Failed to emit analytics with consent checks disabled. ${e.message || ''}`,
					attributes: {
						itemKey: originalItemKey,
						storageType,
						consentChecksDisabled: true,
						wasAsync: wasCheckAsync,
					},
					error: e,
				});
			}
			return;
		}

		if (itemSetByPackage) {
			try {
				// Controls have already been run, passthrough as usual
				return originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
			} catch (e: any) {
				reportSetItemError({
					message: `${storageType} failed to setItem. ${e.message || ''}`,
					attributes: {
						outsidePackage: false,
						storageType,
					},
					error: e,
				});
			}
		}

		const extensionSignals = fg('platform_bsc_browser_extension_detection')
			? captureCallerSignals()
			: undefined;

		// `Storage.setItem` reports failure by throwing and callers such as the availability probes
		// depend on that, so a synchronous write failure has to leave this override. Collected here
		// rather than thrown inline, because the handler that catches it sits inside
		// another handler that would report the same error again.
		let setItemFailure: any;
		try {
			let wasCheckAsync: boolean = true;
			try {
				const productName = getBscGlobalState().product;
				// MaybeSync skips the promise entirely once categories and preferences are loaded, which
				// is what keeps the write and any throw from it synchronous.
				// Attempt to set a known item key that was set from outside the package
				const processingCheck = isProcessingAllowedByPreferencesMaybeSync({
					itemKey: originalItemKey,
					allowedCallback: ({ wasAsync }) => {
						wasCheckAsync = wasAsync;
						sendPackageOperationalEvent({
							action: 'usedAtlBrowserStorageSetItem',
							attributes: {
								itemKey: originalItemKey,
								outsidePackage: !itemSetByPackage,
								storageType,
								wasAsync,
							},
						});
						originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
					},
					blockedCallback: ({ itemHasCategory, itemCategory, wasAsync }) => {
						wasCheckAsync = wasAsync;
						sendPackageOperationalEvent({
							action: 'usedAtlBrowserStorageSetItem',
							attributes: {
								itemKey: originalItemKey,
								outsidePackage: !itemSetByPackage,
								storageType,
								wasAsync,
							},
						});

						if (
							!itemHasCategory &&
							shouldReportUncategorizedItemSet(categoryStorageType, originalItemKey)
						) {
							sendTrackEvent({
								source: 'package',
								action: 'package',
								actionSubject: 'uncategorizedWebStorageItemSet',
								attributes: {
									storageType,
									productName,
									originalItemKey,
									itemCategory: itemCategory ?? null,
									wasAsync,
									cacheLoadedDate: getBscGlobalState().cacheLoadedDate ?? null,
								},
								onSent: uncategorizedItemSetOnSent(categoryStorageType, originalItemKey),
							});
						}

						const {
							allowUncategorizedCookiesInOverrides,
							allowUncategorizedItemsInWebStorageOverrides,
						} = getBscGlobalState();

						// allowUncategorizedCookiesInOverrides impacts both web storage and cookies due to historical reasons
						const allowUncategorized =
							allowUncategorizedCookiesInOverrides || allowUncategorizedItemsInWebStorageOverrides;

						if (allowUncategorized && !itemHasCategory) {
							originalSetItem.apply(storageContext, [originalItemKey, originalItemValue]);
						} else {
							if (!itemHasCategory) {
								Logger.warn(
									`${storageType}.setItem('${originalItemKey}', ...) was blocked` +
										`${productName ? ` for product "${productName}"` : ''}` +
										`: '${originalItemKey}' is not registered in the BSC data store. Add it to the data store before calling this API.`,
								);
							}
							return;
						}
					},
					storageType: categoryStorageType,
					productName,
					extensionSignals,
				});
				if (isPromise<boolean>(processingCheck)) {
					processingCheck.catch((e: any) => {
						reportSetItemError({
							message: `Failed to set item. ${e?.message || ''}`,
							attributes: {
								itemKey: originalItemKey,
								storageType,
								wasAsync: true,
							},
							error: e,
						});
					});
				}
			} catch (e: any) {
				reportSetItemError({
					message: `Failed to set item. ${e.message || ''}`,
					attributes: {
						itemKey: originalItemKey,
						storageType,
						wasAsync: wasCheckAsync,
					},
					error: e,
				});
				setItemFailure = e;
			}
		} catch (e: any) {
			reportSetItemError({
				message: `Failed to send ${storageType} setter event. ${e.message || ''}`,
				error: e,
			});
		}

		if (setItemFailure) {
			throw setItemFailure;
		}
	};
